import React, { useState } from "react";
import { toast } from "react-toastify";
import { requestAuth } from "../../../components/services/RequestService";
import { Request } from "../../../hooks/Request";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  pdf,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";
import {
  ReportPdfHeader,
  ReportPdfFooter,
  formatGeneratedAt,
} from "./reportBranding";
import {
  addReportBranding,
  styleHeaderRow,
  applyZebraFill,
  finalizeReportSheet,
  addExcelClosingNote,
} from "./reportExcelBranding";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    paddingBottom: 50,
    fontSize: 9,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
    flexDirection: "column",
  },
  table: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderRadius: 3,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#94a3b8",
    minHeight: 28,
    alignItems: "stretch",
  },
  tableRowAlt: { backgroundColor: "#eff6ff" },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#1e3a8a",
    color: "#ffffff",
    alignItems: "center",
    minHeight: 28,
  },
  tableColHeader: {
    paddingHorizontal: 6,
    paddingVertical: 6,
    justifyContent: "center",
  },
  tableCellHeader: { fontSize: 8, fontWeight: "bold", color: "#ffffff" },
  tableCol: {
    paddingHorizontal: 6,
    paddingVertical: 6,
    justifyContent: "center",
  },
  tableCell: { fontSize: 8, color: "#334155", lineHeight: 1.3 },
});

const ReportIncidentsPDF = ({ data }) => {
  const today = formatGeneratedAt();
  const totalGroups = new Set(
    data.map((row) => row._mergeInfo?.groupIdx ?? row),
  ).size;

  return (
    <Document>
      <Page size="LEGAL" orientation="landscape" style={styles.page}>
        <ReportPdfHeader
          title="Reporte de Incidencias"
          totalRecords={totalGroups}
          generatedAt={today}
        />
        <View style={styles.table}>
          <View
            style={[
              styles.tableHeaderRow,
              {
                minHeight: 16,
                borderTopLeftRadius: 3,
                borderTopRightRadius: 3,
                borderBottomWidth: 1,
                borderBottomColor: "#ffffff",
              },
            ]}
            fixed
          >
            <View
              style={[
                styles.tableColHeader,
                {
                  width: "30%",
                  borderRightWidth: 1,
                  borderRightColor: "#ffffff",
                },
              ]}
            >
              <Text style={[styles.tableCellHeader, { textAlign: "center" }]}>
                DATOS GENERALES
              </Text>
            </View>
            <View style={[styles.tableColHeader, { width: "70%" }]}>
              <Text style={[styles.tableCellHeader, { textAlign: "center" }]}>
                RECLAMOS
              </Text>
            </View>
          </View>
          <View style={styles.tableHeaderRow} fixed>
            <View style={[styles.tableColHeader, { width: "15%" }]}>
              <Text style={styles.tableCellHeader}>Nro Oper.</Text>
            </View>
            <View
              style={[
                styles.tableColHeader,
                {
                  width: "15%",
                  borderRightWidth: 1,
                  borderRightColor: "#ffffff",
                },
              ]}
            >
              <Text style={styles.tableCellHeader}>Proveedor Gral.</Text>
            </View>
            <View style={[styles.tableColHeader, { width: "35%" }]}>
              <Text style={styles.tableCellHeader}>Descripción</Text>
            </View>
            <View style={[styles.tableColHeader, { width: "20%" }]}>
              <Text style={styles.tableCellHeader}>Fechas / Quién</Text>
            </View>
            <View style={[styles.tableColHeader, { width: "15%" }]}>
              <Text style={styles.tableCellHeader}>Estado</Text>
            </View>
          </View>
          {data.map((row, i) => {
            const isAlt =
              (row._mergeInfo ? row._mergeInfo.groupIdx : i) % 2 !== 0;
            const hideBottomBorder = row._mergeInfo && !row._mergeInfo.isLast;
            const isContinuation = row._mergeInfo && !row._mergeInfo.isFirst;
            return (
              <View
                style={[
                  styles.tableRow,
                  isAlt ? styles.tableRowAlt : {},
                  hideBottomBorder ? { borderBottomWidth: 0 } : {},
                ]}
                key={i}
                wrap={false}
              >
                <View style={[styles.tableCol, { width: "15%" }]}>
                  <Text style={styles.tableCell}>
                    {!isContinuation ? row["Nro Operación"] : ""}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    {
                      width: "15%",
                      borderRightWidth: 1,
                      borderRightColor: "#94a3b8",
                    },
                  ]}
                >
                  <Text style={styles.tableCell}>
                    {!isContinuation ? row.Proveedor : ""}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    {
                      width: "35%",
                      borderTopWidth: isContinuation ? 1 : 0,
                      borderTopColor: "#cbd5e1",
                    },
                  ]}
                >
                  <Text style={styles.tableCell}>
                    {row["Reclamo - Descripción"]}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    {
                      width: "20%",
                      borderTopWidth: isContinuation ? 1 : 0,
                      borderTopColor: "#cbd5e1",
                    },
                  ]}
                >
                  <Text style={styles.tableCell}>
                    {row["Reclamo - Descripción"] !== ""
                      ? `Det: ${row["Reclamo - F. Detección"]}\nRec: ${row["Reclamo - F. Reclamo"]}\nPor: ${row["Reclamo - Quién Detectó"]}`
                      : ""}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    {
                      width: "15%",
                      borderTopWidth: isContinuation ? 1 : 0,
                      borderTopColor: "#cbd5e1",
                    },
                  ]}
                >
                  <Text style={styles.tableCell}>
                    {row["Reclamo - Estado"]}
                  </Text>
                </View>
              </View>
            );
          })}
        </View>
        <ReportPdfFooter />
      </Page>
    </Document>
  );
};

export const ReportIncidentsModal = ({ onClose }) => {
  const [filters, setFilters] = useState({
    nroOperacion: "",
    tipoImportacion: "",
    proveedor: "",
    estado: "",
  });
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const { data: typesImport } = Request({
    urlApi: "/Dropdown/types-import/options?PageNumber=1",
  });
  const { data: providers } = Request({
    urlApi: "/Dropdown/providers/options?PageNumber=1",
  });

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const url = `/Procedure/pag?Fieldnames=codigoDeInterno&Fieldnames=proveedor&Fieldnames=tipoDeImportacion&Fieldnames=registroDeReclamos&IsPaginado=false`;

      const response = await requestAuth("get", url);
      let data = response.data?.data || response.data || [];
      console.log(data);
      // Filtrar localmente según lo seleccionado
      if (filters.nroOperacion) {
        data = data.filter((item) =>
          item.codigoDeInterno
            ?.toLowerCase()
            .includes(filters.nroOperacion.toLowerCase()),
        );
      }
      if (filters.tipoImportacion) {
        data = data.filter(
          (item) => item.tipoDeImportacion === filters.tipoImportacion,
        );
      }
      if (filters.proveedor) {
        data = data.filter((item) => item.proveedor === filters.proveedor);
      }

      data = data.map((item) => {
        let newItem = { ...item };

        let reclamos = newItem.registroDeReclamos;
        if (typeof reclamos === "string") {
          try {
            reclamos = JSON.parse(reclamos);
          } catch (e) {
            reclamos = [];
          }
        }
        if (!Array.isArray(reclamos)) reclamos = [];

        newItem.reclamosParsed = reclamos.map((r) => {
          if (r.description2 && r.description2.startsWith("{")) {
            try {
              return JSON.parse(r.description2);
            } catch (e) {}
          }
          return {
            descripcion: r.label || "",
            fechaDeteccion: r.description3 || "",
            quienDetecta: r.quienDetecta || "",
            fechaReclamo: r.description4 || "",
            estado: r.description5 || "",
          };
        });

        return newItem;
      });

      if (filters.estado) {
        data = data
          .map((item) => ({
            ...item,
            reclamosParsed: item.reclamosParsed.filter(
              (reclamo) =>
                reclamo.estado?.trim().toLowerCase() ===
                filters.estado.trim().toLowerCase(),
            ),
          }))
          .filter((item) => item.reclamosParsed.length > 0);
      }

      // Filtrar aquellos que no tengan ninguna incidencia registrada
      data = data.filter((item) => item.reclamosParsed.length > 0);

      setReportData(data);
      toast.success("Reporte generado exitosamente");
    } catch (error) {
      console.error(error);
      toast.error("Error al generar el reporte");
    } finally {
      setLoading(false);
    }
  };

  const getFlattenedData = () => {
    const flattened = [];
    let groupIdx = 0;

    reportData.forEach((item) => {
      const rArr = item.reclamosParsed || [];
      const maxLen = Math.max(rArr.length, 1);

      for (let i = 0; i < maxLen; i++) {
        const r = rArr[i];

        flattened.push({
          "Nro Operación": i === 0 ? item.codigoDeInterno || "-" : "",
          Proveedor: i === 0 ? item.proveedor || "-" : "",

          "Reclamo - Descripción": r ? r.descripcion || "" : "",
          "Reclamo - F. Detección": r ? r.fechaDeteccion || "" : "",
          "Reclamo - Quién Detectó": r ? r.quienDetecta || "" : "",
          "Reclamo - F. Reclamo": r ? r.fechaReclamo || "" : "",
          "Reclamo - Estado": r ? r.estado || "" : "",

          _mergeInfo: {
            rowspan: i === 0 ? maxLen : 0,
            isFirst: i === 0,
            isLast: i === maxLen - 1,
            groupIdx,
          },
        });
      }
      groupIdx++;
    });
    return flattened;
  };

  const handleDownloadExcel = async () => {
    if (!reportData || reportData.length === 0) {
      toast.warning("No hay datos para exportar");
      return;
    }
    const data = getFlattenedData();
    const generatedAt = formatGeneratedAt();
    const totalGroups = new Set(
      data.map((row) => row._mergeInfo?.groupIdx ?? row),
    ).size;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("REPORTE_DE_INCIDENCIAS");
    const COLUMN_DEFS = [
      { header: "N° IMPORTACION", key: "nro", width: 20 },
      { header: "PROVEEDOR", key: "proveedor", width: 25 },
      { header: "DESCRIPCION", key: "rDesc", width: 40 },
      { header: "FECHA / RESPONSABLE DETECCION", key: "rFechaResp", width: 32 },
      { header: "ESTADO", key: "rEst", width: 15 },
    ];

    worksheet.columns = COLUMN_DEFS.map(({ header, ...rest }) => rest);

    const headerRow = await addReportBranding(workbook, worksheet, {
      title: "Reporte de Incidencias",
      colCount: COLUMN_DEFS.length,
      totalRecords: totalGroups,
      generatedAt,
    });

    worksheet.getRow(headerRow).values = COLUMN_DEFS.map((c) => c.header);
    styleHeaderRow(worksheet.getRow(headerRow));

    let rowIndex = headerRow + 1;
    data.forEach((row) => {
      const fechaResp = [
        row["Reclamo - F. Detección"],
        row["Reclamo - Quién Detectó"],
      ]
        .filter(Boolean)
        .join(" / ");
      const isAlt =
        (row._mergeInfo ? row._mergeInfo.groupIdx : rowIndex) % 2 !== 0;
      const excelRow = worksheet.addRow({
        nro: row["Nro Operación"],
        proveedor: row.Proveedor,
        rDesc: row["Reclamo - Descripción"],
        rFechaResp: fechaResp,
        rEst: row["Reclamo - Estado"],
      });
      applyZebraFill(excelRow, isAlt);

      if (
        row._mergeInfo &&
        row._mergeInfo.isFirst &&
        row._mergeInfo.rowspan > 1
      ) {
        const span = row._mergeInfo.rowspan;
        worksheet.mergeCells(`A${rowIndex}:A${rowIndex + span - 1}`);
        worksheet.mergeCells(`B${rowIndex}:B${rowIndex + span - 1}`);
        ["A", "B"].forEach((col) => {
          worksheet.getCell(`${col}${rowIndex}`).alignment = {
            vertical: "middle",
            horizontal: "left",
            wrapText: true,
          };
        });
      }
      rowIndex++;
    });

    const lastDataRow = rowIndex - 1;
    finalizeReportSheet(worksheet, {
      headerTopRow: headerRow,
      lastDataRow,
      colCount: COLUMN_DEFS.length,
    });
    addExcelClosingNote(worksheet, {
      row: lastDataRow + 2,
      colCount: COLUMN_DEFS.length,
      generatedAt,
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "REPORTE_DE_INCIDENCIAS.xlsx");
    toast.success("Excel descargado exitosamente");
  };

  const handleDownloadPDF = async () => {
    if (!reportData || reportData.length === 0) {
      toast.warning("No hay datos para exportar");
      return;
    }
    toast.info("Generando PDF, por favor espere...");
    try {
      const data = getFlattenedData();
      const doc = <ReportIncidentsPDF data={data} />;
      const asPdf = pdf([]);
      asPdf.updateContainer(doc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "REPORTE_DE_INCIDENCIAS.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF descargado exitosamente");
    } catch (error) {
      console.error(error);
      toast.error("Error al generar PDF");
    }
  };

  const renderReclamos = (reclamosRaw) => {
    if (!reclamosRaw || reclamosRaw.length === 0)
      return <span className="text-gray-400 text-xs italic">Sin reclamos</span>;

    return (
      <table className="w-full text-xs text-left border border-gray-200 bg-white">
        <thead className="bg-gray-200 border-b border-gray-300 text-gray-800">
          <tr>
            <th className="p-1 font-semibold">Descripción</th>
            <th className="p-1 font-semibold w-24">Fechas</th>
            <th className="p-1 font-semibold w-24">Quién detectó</th>
            <th className="p-1 font-semibold text-center w-20">Estado</th>
          </tr>
        </thead>
        <tbody>
          {reclamosRaw.map((item, idx) => {
            let badgeClass = "bg-gray-100 text-gray-800";
            if (item.estado === "En proceso")
              badgeClass = "bg-yellow-100 text-yellow-800";
            if (item.estado === "Abierto")
              badgeClass = "bg-blue-100 text-blue-800";
            if (item.estado === "Cerrado")
              badgeClass = "bg-green-100 text-green-800";

            return (
              <tr key={idx} className="border-b border-gray-100">
                <td
                  className="p-1 max-w-[150px] truncate font-medium text-gray-800"
                  title={item.descripcion}
                >
                  {item.descripcion || "-"}
                </td>
                <td className="p-1 text-[10px] text-gray-600">
                  <div>
                    <span className="font-semibold">D:</span>{" "}
                    {item.fechaDeteccion || "-"}
                  </div>
                  <div>
                    <span className="font-semibold">R:</span>{" "}
                    {item.fechaReclamo || "-"}
                  </div>
                </td>
                <td
                  className="p-1 text-[10px] text-gray-600 truncate"
                  title={item.quienDetecta}
                >
                  {item.quienDetecta || "-"}
                </td>
                <td className="p-1 text-center align-middle">
                  <span
                    className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${badgeClass}`}
                  >
                    {item.estado || "-"}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-[1200px] max-h-[90vh] flex flex-col relative overflow-hidden uppercase">
      {/* Cabecera Fija */}
      <div className="px-4 py-3 md:px-6 md:py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white flex justify-between items-center shrink-0">
        <h2 className="text-lg md:text-xl font-bold text-gray-800 m-0">
          REPORTE DE INCIDENCIAS
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-red-500 transition-colors"
        >
          <i className="fas fa-times text-xl"></i>
        </button>
      </div>

      {/* Cuerpo Scrolleable */}
      <div className="p-4 md:p-6 overflow-y-auto flex-1 bg-white">
        <div className="py-2 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nro Operación
              </label>
              <input
                type="text"
                placeholder="Buscar..."
                value={filters.nroOperacion}
                onChange={(e) =>
                  setFilters({ ...filters, nroOperacion: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Importación
              </label>
              <select
                value={filters.tipoImportacion}
                onChange={(e) =>
                  setFilters({ ...filters, tipoImportacion: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 transition-colors bg-white"
              >
                <option value="">Todos</option>
                {typesImport?.map((t, i) => (
                  <option key={i} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Proveedor
              </label>
              <select
                value={filters.proveedor}
                onChange={(e) =>
                  setFilters({ ...filters, proveedor: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 transition-colors bg-white"
              >
                <option value="">Todos</option>
                {providers?.map((p, i) => (
                  <option key={i} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>

              <select
                value={filters.estado}
                onChange={(e) =>
                  setFilters({ ...filters, estado: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 transition-colors bg-white"
              >
                <option value="">Todos</option>
                <option value="Abierto">Abierto</option>
                <option value="En proceso">En proceso</option>
                <option value="Cerrado">Cerrado</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-3 mt-4 border-b border-gray-200 pb-4">
          <button
            className={`px-4 py-2 text-white rounded-md transition-colors flex items-center gap-2 ${!reportData ? "bg-green-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}
            onClick={handleDownloadExcel}
            disabled={!reportData || loading}
          >
            <i className="fas fa-file-excel"></i>
            <span>Descargar Excel</span>
          </button>
          <button
            className={`px-4 py-2 text-white rounded-md transition-colors flex items-center gap-2 ${!reportData ? "bg-red-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
            onClick={handleDownloadPDF}
            disabled={!reportData || loading}
          >
            <i className="fas fa-file-pdf"></i>
            <span>Descargar PDF</span>
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            onClick={handleGenerate}
            disabled={loading}
          >
            <i
              className={
                loading ? "fas fa-spinner fa-spin mr-2" : "fas fa-eye mr-2"
              }
            ></i>
            <span>{loading ? "Cargando..." : "Previsualizar"}</span>
          </button>
        </div>

        {reportData && (
          <div className="overflow-x-auto border border-gray-200 rounded-md shadow-sm mt-4">
            <table className="w-full text-xs text-left">
              <thead className="bg-[#1e3a8a] text-white font-bold border-b border-gray-300 shadow-sm">
                <tr>
                  <th className="px-3 py-2 whitespace-nowrap">
                    N° IMPORTACION
                  </th>
                  <th className="px-3 py-2 whitespace-nowrap">PROVEEDOR</th>
                  <th className="px-3 py-2 min-w-[250px]">DESCRIPCION</th>
                  <th className="px-3 py-2 whitespace-nowrap">
                    FECHA / RESPONSABLE DETECCION
                  </th>
                  <th className="px-3 py-2 whitespace-nowrap">ESTADO</th>
                </tr>
              </thead>
              <tbody>
                {reportData.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-6 text-gray-500">
                      No se encontraron resultados para los filtros
                      seleccionados (Solo se muestran trámites con incidencias).
                    </td>
                  </tr>
                ) : (
                  getFlattenedData().map((row, idx) => {
                    const isFirst = row._mergeInfo
                      ? row._mergeInfo.isFirst
                      : true;
                    const rowspan = row._mergeInfo ? row._mergeInfo.rowspan : 1;
                    const fechaResp = [
                      row["Reclamo - F. Detección"],
                      row["Reclamo - Quién Detectó"],
                    ]
                      .filter(Boolean)
                      .join(" / ");

                    let badgeClass = "bg-gray-100 text-gray-800";
                    const estado = row["Reclamo - Estado"] || "";
                    if (estado === "En proceso")
                      badgeClass = "bg-yellow-100 text-yellow-800";
                    if (estado === "Abierto")
                      badgeClass = "bg-blue-100 text-blue-800";
                    if (estado === "Cerrado")
                      badgeClass = "bg-green-100 text-green-800";

                    return (
                      <tr
                        key={idx}
                        className={`border-b border-gray-200 align-middle transition-colors ${
                          row._mergeInfo && row._mergeInfo.groupIdx % 2 === 0
                            ? "bg-white"
                            : "bg-blue-50/50"
                        } hover:bg-blue-100/50`}
                      >
                        {isFirst && (
                          <td
                            rowSpan={rowspan}
                            className="px-3 py-2 font-medium text-gray-900 border-r border-gray-200"
                          >
                            {row["Nro Operación"]}
                          </td>
                        )}
                        {isFirst && (
                          <td
                            rowSpan={rowspan}
                            className="px-3 py-2 border-r border-gray-200"
                          >
                            {row.Proveedor}
                          </td>
                        )}
                        <td className="px-3 py-2 border-r border-gray-100">
                          {row["Reclamo - Descripción"] || "-"}
                        </td>
                        <td className="px-3 py-2 border-r border-gray-100 text-gray-600">
                          {fechaResp || "-"}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {estado ? (
                            <span
                              className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${badgeClass}`}
                            >
                              {estado}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pie Fijo */}
      <div className="px-4 py-3 md:px-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-2 shrink-0">
        <button
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-medium shadow-sm"
          onClick={onClose}
        >
          Cerrar
        </button>
      </div>
    </div>
  );
};
